import { t } from '../i18n';
// client/admin-client/src/components/MarqueeManager.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../api/axios';
function getAuthHeaders() {
    const token = localStorage.getItem('token') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
}
// datetime-local(local) -> UTC ISO(Z)
function localToUtcIso(localStr) {
    if (!localStr)
        return null;
    const d = new Date(localStr); // local time
    if (Number.isNaN(d.getTime()))
        return null;
    return d.toISOString();
}
// UTC ISO -> datetime-local string(로컬 기준)
function utcIsoToLocalInput(utcIso) {
    if (!utcIso)
        return '';
    const d = new Date(utcIso);
    if (Number.isNaN(d.getTime()))
        return '';
    const offsetMin = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offsetMin * 60000);
    return local.toISOString().slice(0, 16);
}
function toLocalText(utcIso) {
    if (!utcIso)
        return '-';
    const d = new Date(utcIso);
    if (Number.isNaN(d.getTime()))
        return '-';
    return d.toLocaleString();
}
const STATUS_OPTIONS = ['waiting', 'running', 'ended', 'disabled'];
const DEFAULT_CREATE = {
    message: '',
    link_url: '',
    index: '',
    reserve_enabled: false,
    reserve_local: '',
    status: '', // 비워두면 서버가 reserve 기준 자동 결정
};
export default function MarqueeManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [createForm, setCreateForm] = useState(DEFAULT_CREATE);
    // edit states
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState(null);
    const sortedItems = useMemo(() => {
        const arr = Array.isArray(items) ? [...items] : [];
        arr.sort((a, b) => {
            const ia = Number(a?.index ?? 0);
            const ib = Number(b?.index ?? 0);
            if (ia !== ib)
                return ia - ib;
            return Number(a?.id ?? 0) - Number(b?.id ?? 0);
        });
        return arr;
    }, [items]);
    const fetchList = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const qs = filterStatus && filterStatus !== 'all' ? `?status=${encodeURIComponent(filterStatus)}` : '';
            const res = await axios.get(`/admin/community/marquee${qs}`, { headers: getAuthHeaders() });
            setItems(res.data?.items || []);
        }
        catch (e) {
            setError(e?.response?.data?.message || t("\uC804\uAD11\uD310 \uBAA9\uB85D \uC870\uD68C \uC2E4\uD328"));
        }
        finally {
            setLoading(false);
        }
    }, [filterStatus]);
    useEffect(() => {
        fetchList();
    }, [fetchList]);
    async function createOne(e) {
        e.preventDefault();
        if (busy)
            return;
        const msg = String(createForm.message || '').trim();
        if (!msg) {
            setError(t("message\uB294 \uD544\uC218\uC785\uB2C8\uB2E4."));
            return;
        }
        const payload = {
            message: msg,
            link_url: createForm.link_url ? String(createForm.link_url).trim() : null,
        };
        const idx = Number(createForm.index);
        if (Number.isFinite(idx))
            payload.index = idx;
        if (createForm.reserve_enabled) {
            const iso = localToUtcIso(createForm.reserve_local);
            if (!iso) {
                setError(t("\uC608\uC57D \uC2DC\uAC04\uC774 \uC62C\uBC14\uB974\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4."));
                return;
            }
            payload.reserve_time_utc = iso;
        }
        else {
            payload.reserve_time_utc = null;
        }
        // status는 선택 입력(비우면 서버가 자동)
        if (createForm.status)
            payload.status = createForm.status;
        setBusy(true);
        setError('');
        try {
            await axios.post('/admin/community/marquee', payload, { headers: getAuthHeaders() });
            setCreateForm(DEFAULT_CREATE);
            await fetchList();
        }
        catch (e2) {
            setError(e2?.response?.data?.message || t("\uC804\uAD11\uD310 \uB4F1\uB85D \uC2E4\uD328"));
        }
        finally {
            setBusy(false);
        }
    }
    function startEdit(row) {
        setEditingId(row.id);
        setEditDraft({
            index: row.index ?? 0,
            message: row.message ?? '',
            link_url: row.link_url ?? '',
            reserve_enabled: Boolean(row.reserve_time_utc),
            reserve_local: row.reserve_time_utc ? utcIsoToLocalInput(row.reserve_time_utc) : '',
            status: row.status ?? 'waiting',
        });
    }
    function cancelEdit() {
        setEditingId(null);
        setEditDraft(null);
    }
    async function saveEdit(row) {
        if (!editDraft)
            return;
        if (busy)
            return;
        const msg = String(editDraft.message || '').trim();
        if (!msg) {
            setError(t("message\uB294 \uBE48\uAC12\uC774 \uB420 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."));
            return;
        }
        const payload = {
            index: Number(editDraft.index),
            message: msg,
            link_url: editDraft.link_url ? String(editDraft.link_url).trim() : null,
            status: editDraft.status ? String(editDraft.status) : 'waiting',
        };
        if (editDraft.reserve_enabled) {
            const iso = localToUtcIso(editDraft.reserve_local);
            if (!iso) {
                setError(t("\uC608\uC57D \uC2DC\uAC04\uC774 \uC62C\uBC14\uB974\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4."));
                return;
            }
            payload.reserve_time_utc = iso;
        }
        else {
            payload.reserve_time_utc = null;
        }
        setBusy(true);
        setError('');
        try {
            await axios.patch(`/admin/community/marquee/${row.id}`, payload, { headers: getAuthHeaders() });
            await fetchList();
            cancelEdit();
        }
        catch (e) {
            setError(e?.response?.data?.message || t("\uC804\uAD11\uD310 \uC218\uC815 \uC2E4\uD328"));
        }
        finally {
            setBusy(false);
        }
    }
    async function removeOne(row) {
        if (busy)
            return;
        if (!window.confirm(t('전광판 삭제?\n(id={id})', { id: row.id })))
            return;
        setBusy(true);
        setError('');
        try {
            await axios.delete(`/admin/community/marquee/${row.id}`, { headers: getAuthHeaders() });
            await fetchList();
        }
        catch (e) {
            setError(e?.response?.data?.message || t("\uC804\uAD11\uD310 \uC0AD\uC81C \uC2E4\uD328"));
        }
        finally {
            setBusy(false);
        }
    }
    // 정렬 저장: 현재 화면에 보이는 index들을 그대로 서버에 반영
    async function saveOrder() {
        if (busy)
            return;
        const payload = {
            items: sortedItems.map((x) => ({ id: x.id, index: Number(x.index ?? 0) })),
        };
        setBusy(true);
        setError('');
        try {
            await axios.post('/admin/community/marquee/reorder', payload, { headers: getAuthHeaders() });
            await fetchList();
        }
        catch (e) {
            setError(e?.response?.data?.message || t("\uC815\uB82C \uC800\uC7A5 \uC2E4\uD328"));
        }
        finally {
            setBusy(false);
        }
    }
    // 예약 tick: waiting -> running 전환
    async function tick() {
        if (busy)
            return;
        setBusy(true);
        setError('');
        try {
            await axios.post('/admin/community/marquee/tick', {}, { headers: getAuthHeaders() });
            await fetchList();
        }
        catch (e) {
            setError(e?.response?.data?.message || t("tick \uC2E4\uD328"));
        }
        finally {
            setBusy(false);
        }
    }
    return (<div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{t("\uC804\uAD11\uD310 \uAD00\uB9AC")}</h1>
          <div className="text-sm text-gray-600">
            marquee_messages: id / index / message / link_url / reserve_time_utc / status
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end">
          <select className="border rounded px-3 py-2" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} disabled={loading}>
            <option value="all">{t("\uC804\uCCB4")}</option>
            {STATUS_OPTIONS.map((s) => (<option key={s} value={s}>
                {s}
              </option>))}
          </select>

          <button onClick={fetchList} className="px-3 py-2 rounded bg-gray-800 text-white hover:bg-gray-700 disabled:opacity-60" disabled={loading || busy}>{t("\uC0C8\uB85C\uACE0\uCE68")}</button>

          <button onClick={tick} className="px-3 py-2 rounded border hover:bg-gray-50 disabled:opacity-60" disabled={loading || busy} title={t("reserve_time_utc\uAC00 \uC9C0\uB09C waiting\uB4E4\uC744 running\uC73C\uB85C \uC804\uD658")}>{t("\uC608\uC57D tick")}</button>

          <button onClick={saveOrder} className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-60" disabled={loading || busy} title={t("\uD604\uC7AC index \uAC12\uB4E4\uC744 \uC11C\uBC84\uC5D0 \uC800\uC7A5")}>{t("\uC815\uB82C \uC800\uC7A5")}</button>
        </div>
      </div>

      {error ? (<div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">{error}</div>) : null}

      {/* Create Form */}
      <form onSubmit={createOne} className="p-4 rounded border bg-white space-y-3">
        <div className="font-semibold">{t("\uC0C8 \uC804\uAD11\uD310 \uB4F1\uB85D")}</div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <label className="flex flex-col gap-1 md:col-span-2">
            <span className="text-sm text-gray-600">{t("\uB0B4\uC6A9(message)")}</span>
            <input className="border rounded px-3 py-2" value={createForm.message} onChange={(e) => setCreateForm((p) => ({ ...p, message: e.target.value }))} placeholder={t("\uC608: NEW YEAR EVENT START!")}/>
          </label>

          <label className="flex flex-col gap-1 md:col-span-2">
            <span className="text-sm text-gray-600">{t("\uD074\uB9AD \uC774\uB3D9 URL(link_url)")}</span>
            <input className="border rounded px-3 py-2" value={createForm.link_url} onChange={(e) => setCreateForm((p) => ({ ...p, link_url: e.target.value }))} placeholder={t("/community/notice/1 \uB610\uB294 https://...")}/>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">{t("index(\uC120\uD0DD)")}</span>
            <input className="border rounded px-3 py-2" type="number" value={createForm.index} onChange={(e) => setCreateForm((p) => ({ ...p, index: e.target.value }))} placeholder={t("\uBBF8\uC785\uB825\uC2DC \uC790\uB3D9")}/>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm text-gray-600">{t("status(\uC120\uD0DD)")}</span>
            <select className="border rounded px-3 py-2" value={createForm.status} onChange={(e) => setCreateForm((p) => ({ ...p, status: e.target.value }))}>
              <option value="">{t("(\uC790\uB3D9)")}</option>
              {STATUS_OPTIONS.map((s) => (<option key={s} value={s}>
                  {s}
                </option>))}
            </select>
          </label>

          <div className="md:col-span-2 border rounded p-3">
            <div className="font-medium">{t("\uC608\uC57D(reserve_time_utc)")}</div>

            <label className="flex items-center gap-2 mt-2">
              <input type="checkbox" checked={createForm.reserve_enabled} onChange={(e) => setCreateForm((p) => ({ ...p, reserve_enabled: e.target.checked }))}/>{t("\uC608\uC57D \uC0AC\uC6A9")}</label>

            {createForm.reserve_enabled ? (<div className="mt-2">
                <input className="border rounded px-3 py-2 w-full" type="datetime-local" value={createForm.reserve_local} onChange={(e) => setCreateForm((p) => ({ ...p, reserve_local: e.target.value }))}/>
                <div className="text-xs text-gray-500 mt-1">{t("* \uC800\uC7A5\uC740 UTC\uB85C \uB4E4\uC5B4\uAC00\uACE0, \uD654\uBA74\uC740 \uB85C\uCEEC \uC2DC\uAC04\uC73C\uB85C \uC785\uB825/\uD45C\uC2DC\uB428")}</div>
              </div>) : (<div className="text-sm text-gray-600 mt-2">{t("\uC608\uC57D \uC5C6\uC74C \u2192 \uC989\uC2DC \uC9C4\uD589(running)\uC73C\uB85C \uC790\uB3D9 \uC124\uC815\uB428")}</div>)}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <button type="submit" disabled={busy || loading} className="px-4 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60">{t("\uB4F1\uB85D")}</button>

          <button type="button" onClick={() => setCreateForm(DEFAULT_CREATE)} className="px-4 py-2 rounded border" disabled={busy || loading}>{t("\uCD08\uAE30\uD654")}</button>
        </div>
      </form>

      {/* List */}
      <div className="p-4 rounded border bg-white">
        {loading ? (<div className="text-gray-600">{t("\uB85C\uB529\uC911...")}</div>) : sortedItems.length ? (<div className="overflow-x-auto">
            <table className="admin-marquee-table min-w-[760px] w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 pr-3">id</th>
                  <th className="py-2 pr-3">index</th>
                  <th className="py-2 pr-3">message</th>
                  <th className="py-2 pr-3">link_url</th>
                  <th className="py-2 pr-3">{t("reserve_time_utc(\uD45C\uC2DC:\uB85C\uCEEC)")}</th>
                  <th className="py-2 pr-3">status</th>
                  <th className="py-2 pr-3">created</th>
                  <th className="py-2 pr-3">actions</th>
                </tr>
              </thead>

              <tbody>
                {sortedItems.map((row) => {
                const isEditing = editingId === row.id;
                return (<tr key={row.id} className="admin-marquee-mobile-row border-b align-top">
                      <td data-label="ID" className="py-2 pr-3 text-gray-700">{row.id}</td>

                      <td data-label="Index" className="py-2 pr-3">
                        {isEditing ? (<input className="border rounded px-2 py-1 w-24" type="number" value={editDraft?.index ?? 0} onChange={(e) => setEditDraft((p) => ({ ...p, index: e.target.value }))}/>) : (<input className="border rounded px-2 py-1 w-24" type="number" value={row.index ?? 0} onChange={(e) => {
                            const v = Number(e.target.value);
                            setItems((prev) => prev.map((x) => (x.id === row.id ? { ...x, index: Number.isFinite(v) ? v : x.index } : x)));
                        }}/>)}
                      </td>

                      <td data-label="Message" className="py-2 pr-3">
                        {isEditing ? (<textarea className="border rounded px-2 py-1 w-[240px]" rows={2} value={editDraft?.message ?? ''} onChange={(e) => setEditDraft((p) => ({ ...p, message: e.target.value }))}/>) : (<div className="w-[240px] whitespace-pre-wrap">{row.message}</div>)}
                      </td>

                      <td data-label="Link URL" className="py-2 pr-3">
                        {isEditing ? (<input className="border rounded px-2 py-1 w-[200px]" value={editDraft?.link_url ?? ''} onChange={(e) => setEditDraft((p) => ({ ...p, link_url: e.target.value }))} placeholder="/..."/>) : row.link_url ? (<div className="w-[200px] break-all text-blue-700">{row.link_url}</div>) : (<span className="text-gray-400">-</span>)}
                      </td>

                      <td data-label={t('예약 시간')} className="py-2 pr-3">
                        {isEditing ? (<div className="space-y-1">
                            <label className="flex items-center gap-2">
                              <input type="checkbox" checked={Boolean(editDraft?.reserve_enabled)} onChange={(e) => setEditDraft((p) => ({
                            ...p,
                            reserve_enabled: e.target.checked,
                            reserve_local: e.target.checked ? p.reserve_local : '',
                        }))}/>{t("\uC608\uC57D \uC0AC\uC6A9")}</label>

                            {editDraft?.reserve_enabled ? (<input className="border rounded px-2 py-1" type="datetime-local" value={editDraft?.reserve_local ?? ''} onChange={(e) => setEditDraft((p) => ({ ...p, reserve_local: e.target.value }))}/>) : (<div className="text-xs text-gray-500">{t("\uC608\uC57D \uC5C6\uC74C")}</div>)}
                          </div>) : (<div className="text-gray-700">{toLocalText(row.reserve_time_utc)}</div>)}
                      </td>

                      <td data-label="Status" className="py-2 pr-3">
                        {isEditing ? (<select className="border rounded px-2 py-1" value={editDraft?.status ?? 'waiting'} onChange={(e) => setEditDraft((p) => ({ ...p, status: e.target.value }))}>
                            {STATUS_OPTIONS.map((s) => (<option key={s} value={s}>
                                {s}
                              </option>))}
                          </select>) : (<span className="font-medium">{row.status}</span>)}
                      </td>

                      <td data-label="Created" className="py-2 pr-3 text-gray-600">
                        {row.created_at ? new Date(row.created_at).toLocaleString() : '-'}
                      </td>

                      <td data-label={t('관리')} className="py-2 pr-3">
                        {isEditing ? (<div className="flex gap-2">
                            <button type="button" className="px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-60" onClick={() => saveEdit(row)} disabled={busy}>{t("\uC800\uC7A5")}</button>
                            <button type="button" className="px-3 py-1.5 rounded border hover:bg-gray-50" onClick={cancelEdit} disabled={busy}>{t("\uCDE8\uC18C")}</button>
                          </div>) : (<div className="flex gap-2">
                            <button type="button" className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-60" onClick={() => startEdit(row)} disabled={busy}>{t("\uC218\uC815")}</button>
                            <button type="button" className="px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-500 disabled:opacity-60" onClick={() => removeOne(row)} disabled={busy}>{t("\uC0AD\uC81C")}</button>
                          </div>)}
                      </td>
                    </tr>);
            })}
              </tbody>
            </table>

            <div className="text-xs text-gray-500 mt-2">{t("* index\uB97C \uBC14\uAFBC \uB4A4")}<b>{t("\uC815\uB82C \uC800\uC7A5")}</b>{t("\uC744 \uB20C\uB7EC\uC57C \uC11C\uBC84\uC5D0 \uBC18\uC601\uB428")}</div>
          </div>) : (<div className="text-gray-600">{t("\uB370\uC774\uD130 \uC5C6\uC74C")}</div>)}
      </div>
    </div>);
}
